import { useRouterState } from '@tanstack/react-router';
import { Bell, PanelLeft, Plus } from 'lucide-react';
import { useMe } from '@/features/identity/useMe';
import { useNewDeal } from '@/features/pipeline/newDealStore';
import { GlobalSearch } from '@/features/search/GlobalSearch';
import { UserMenu } from './UserMenu';
import { useShellStore } from './shellStore';

/**
 * Topbar do shell (DOCUMENTACAO.md §3.2, Portal Vega Bloco 1 — DEC-060): botão de recolher a
 * sidebar (☰), título "Módulo · Aba", busca global, +Novo Deal (só no módulo Pipeline),
 * notificações, config admin (gated) e perfil. Busca/+Novo Deal/notificações/config seguem
 * placeholders funcionais desde a Onda 1 — comportamento real vem em E6/E7/E8.
 *
 * Ordem importa: prefixos mais específicos (ex. /pipeline/tarefas) precisam vir antes do prefixo
 * genérico do módulo (/pipeline), senão o find() casa cedo demais.
 */
const PAGE_TITLES: { prefix: string; title: string }[] = [
  { prefix: '/', title: 'Início' },
  { prefix: '/pipeline/tarefas', title: 'Pipeline · Tarefas' },
  { prefix: '/pipeline/originacao', title: 'Pipeline · Originação' },
  { prefix: '/pipeline/dashboards', title: 'Pipeline · Dashboards' },
  { prefix: '/pipeline', title: 'Pipeline · Funil' },
  { prefix: '/portfolio/tarefas', title: 'Portfólio · Tarefas' },
  { prefix: '/portfolio', title: 'Portfólio' },
  { prefix: '/ri', title: 'Investidores' },
  { prefix: '/config', title: 'Configurações' },
];

function pageTitle(pathname: string): string {
  const hit = PAGE_TITLES.find((p) => pathname === p.prefix || pathname.startsWith(`${p.prefix}/`));
  return hit?.title ?? 'Portal Vega';
}

export function Topbar() {
  const me = useMe(true);
  const user = me.data;
  const toggleSidebar = useShellStore((s) => s.toggleSidebar);
  const openNewDeal = useNewDeal((s) => s.openNew);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // +Novo Deal deixa de ser ação global (DEC-060, item 5) — só faz sentido dentro do Pipeline.
  const isPipelineModule = pathname === '/pipeline' || pathname.startsWith('/pipeline/');

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b border-border bg-card px-4">
      <button
        type="button"
        onClick={toggleSidebar}
        title="Recolher/expandir o menu"
        aria-label="Recolher ou expandir o menu lateral"
        className="rounded p-1.5 text-muted-foreground hover:bg-secondary"
      >
        <PanelLeft size={16} strokeWidth={1.6} />
      </button>

      <h2 className="shrink-0 text-sm font-semibold tracking-tight">{pageTitle(pathname)}</h2>

      <GlobalSearch />

      {isPipelineModule && (
        <button
          type="button"
          onClick={openNewDeal}
          title="Registrar novo deal"
          className="ml-auto inline-flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
        >
          <Plus size={14} strokeWidth={2} />
          Novo Deal
        </button>
      )}

      <button
        type="button"
        title="Notificações (em breve)"
        aria-label="Notificações"
        className={`rounded p-1.5 text-muted-foreground hover:bg-secondary ${isPipelineModule ? '' : 'ml-auto'}`}
      >
        <Bell size={16} strokeWidth={1.6} />
      </button>

      <div className="flex items-center border-l border-border pl-3">
        <UserMenu user={user} />
      </div>
    </header>
  );
}
